import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Loader2, Edit2, UserX, User, Phone, Mail, UserCheck, UserMinus, CalendarDays, Clock, RotateCcw } from 'lucide-react';
import RequirePermission from '../../components/AccessControl/RequirePermission';
import { useStore } from '../../context/StoreContext';
import { SMIIFY_API } from '../../config/api';
import styles from './ManageStaff.module.css';

const STAFF_API = `${SMIIFY_API}/staff`;
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const to12 = (t) => { if (!t) return '—'; const [h, m] = t.split(':').map(Number); return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`; };

const ManageStaff = () => {
    const navigate = useNavigate();
    const { activeStore } = useStore();
    const storeId = activeStore?.id || activeStore?._id;

    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('All');
    const [statusView, setStatusView] = useState('Active');
    const [staffList, setStaffList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [activeStaffId, setActiveStaffId] = useState(null);

    const authHeaders = useCallback(() => ({
        Authorization: `Bearer ${localStorage.getItem('smiify_token')}`,
        ...(storeId ? { 'x-store-id': String(storeId) } : {}),
    }), [storeId]);

    const fetchStaff = useCallback(async () => {
        if (!storeId || storeId === 'global_hq') { setStaffList([]); return; }
        setIsLoading(true);
        setError('');
        try {
            const res = await fetch(`${STAFF_API}?storeId=${encodeURIComponent(storeId)}&includeInactive=true`, { headers: authHeaders() });
            const data = await res.json().catch(() => ({}));
            if (res.ok && data.success) setStaffList(data.data || []);
            else setError(data.message || 'Could not load staff.');
        } catch {
            setError('Cannot reach the server. Try again in a minute.');
        } finally {
            setIsLoading(false);
        }
    }, [storeId, authHeaders]);

    useEffect(() => { fetchStaff(); }, [fetchStaff]);

    const changeStatus = async (staff, action, e) => {
        e.stopPropagation();
        const text = action === 'deactivate'
            ? `Deactivate ${staff.name}? They will be hidden from billing, appointments and attendance.`
            : `Make ${staff.name} active again?`;
        if (!window.confirm(text)) return;
        try {
            const res = await fetch(`${STAFF_API}/${staff.id}/${action}`, { method: 'PATCH', headers: authHeaders() });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.success) throw new Error(data.message || 'Could not change status.');
            setStaffList((list) => list.map((s) => (s.id === staff.id ? { ...s, ...data.data } : s)));
        } catch (err) {
            alert(err.message === 'Failed to fetch' ? 'Cannot reach the server.' : err.message);
        }
    };

    const byStatus = useMemo(() => staffList.filter((s) => (statusView === 'Active' ? s.status === 'Active' : s.status !== 'Active')), [staffList, statusView]);
    const uniqueRoles = useMemo(() => ['All', ...new Set(byStatus.map((s) => s.designation).filter(Boolean))], [byStatus]);
    const activeStaff = useMemo(() => staffList.find((s) => s.id === activeStaffId) || null, [staffList, activeStaffId]);
    const counts = useMemo(() => ({ active: staffList.filter((s) => s.status === 'Active').length, inactive: staffList.filter((s) => s.status !== 'Active').length }), [staffList]);

    const filteredStaff = byStatus.filter((staff) => {
        const q = searchTerm.toLowerCase();
        const matchesSearch = !q || staff.name.toLowerCase().includes(q) || String(staff.phone || '').includes(q) || String(staff.employeeId || '').toLowerCase().includes(q);
        return matchesSearch && (activeTab === 'All' || staff.designation === activeTab);
    });

    const initialsOf = (s) => (`${s.firstName?.[0] || ''}${s.lastName?.[0] || ''}`.toUpperCase() || s.name.slice(0, 2).toUpperCase());

    return (
        <div className={styles.pageWrapper}>
            <div className={styles.fixedHeader}>
                <div className={styles.headerTopRow}>
                    <div className={styles.titleSection}>
                        <h1 className={styles.pageTitle}>Staff Management</h1>
                        <p className={styles.pageSubtitle}>Profiles, salary, shift, leave and attendance rules.</p>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" className={styles.primaryBtnFull} style={{ width: 'auto', padding: '6px 12px', fontSize: 12 }} onClick={() => navigate('/attendance')}>
                            <CalendarDays size={14} /> Attendance
                        </button>
                        <RequirePermission module="staff" action="create">
                            <button type="button" className={styles.primaryBtn} onClick={() => navigate('/staff/new')}>
                                <Plus size={14} /> <span>Add New Staff</span>
                            </button>
                        </RequirePermission>
                    </div>
                </div>

                <div className={styles.headerBottomRow}>
                    <div className={styles.tabsContainer}>
                        <button type="button" className={`${styles.tabBtn} ${statusView === 'Active' ? styles.activeTab : ''}`} onClick={() => { setStatusView('Active'); setActiveTab('All'); }}>Active ({counts.active})</button>
                        <button type="button" className={`${styles.tabBtn} ${statusView === 'Inactive' ? styles.activeTab : ''}`} onClick={() => { setStatusView('Inactive'); setActiveTab('All'); }}>Inactive ({counts.inactive})</button>
                        <span style={{ width: 1, background: 'var(--border-color)', margin: '0 6px' }} />
                        {uniqueRoles.map((tab) => (
                            <button type="button" key={tab} className={`${styles.tabBtn} ${activeTab === tab ? styles.activeTab : ''}`} onClick={() => setActiveTab(tab)}>{tab}</button>
                        ))}
                    </div>
                    <div className={styles.controlsSection}>
                        <div className={styles.searchBox}>
                            <Search size={14} className={styles.searchIcon} />
                            <input type="text" placeholder="Search name, phone or employee ID..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className={styles.searchInput} />
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles.contentArea}>
                {isLoading ? (
                    <div className={styles.loaderBox}><Loader2 size={24} className={styles.spinner} /><p>Loading staff...</p></div>
                ) : (
                    <div className={styles.splitLayoutWrapper}>
                        <div className={styles.leftPanel}>
                            <div className={styles.tableContainer}>
                                <div className={styles.tableScrollArea}>
                                    <table className={styles.dataTable}>
                                        <thead>
                                            <tr>
                                                <th className={styles.w40} />
                                                <th>Staff</th>
                                                <th>Role</th>
                                                <th>Shift</th>
                                                <th>Contact</th>
                                                <th>Status</th>
                                                <th className={styles.textRight}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {error ? (
                                                <tr><td colSpan="7" className={styles.emptyState}><h3>{error}</h3></td></tr>
                                            ) : filteredStaff.length > 0 ? filteredStaff.map((staff) => {
                                                const selected = activeStaffId === staff.id;
                                                return (
                                                    <tr key={staff.id} className={`${styles.mainRow} ${selected ? styles.activeMainRow : ''}`} onClick={() => setActiveStaffId(staff.id)} onDoubleClick={() => navigate(`/staff/${staff.id}`)}>
                                                        <td className={styles.indicatorCell}>{selected && <div className={styles.activeIndicator} />}</td>
                                                        <td>
                                                            <div className={styles.staffNameWrap}>
                                                                <div className={styles.avatarBox}>{initialsOf(staff) || <User size={12} />}</div>
                                                                <div className={styles.staffInfo}>
                                                                    <span className={styles.staffName}>{staff.name}</span>
                                                                    <span className={styles.staffId}>{staff.employeeId ? `ID ${staff.employeeId}` : `#${staff.id.slice(-4).toUpperCase()}`}</span>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td><span className={styles.roleBadge}>{staff.designation || '—'}</span></td>
                                                        <td><span className={styles.phoneText}><Clock size={10} /> {to12(staff.shift?.start)} to {to12(staff.shift?.end)}</span></td>
                                                        <td><span className={styles.phoneText}><Phone size={10} /> {staff.phone || '—'}</span></td>
                                                        <td><span className={staff.status === 'Active' ? styles.statusActive : styles.statusInactive}>{staff.status === 'Active' ? 'Active' : 'Inactive'}</span></td>
                                                        <td className={styles.actionCells} onClick={(e) => e.stopPropagation()}>
                                                            <div className={styles.defaultActions}>
                                                                <RequirePermission module="staff" action="edit">
                                                                    <button type="button" className={styles.iconBtn} title="Open full profile" aria-label={`Edit ${staff.name}`} onClick={() => navigate(`/staff/${staff.id}`)}><Edit2 size={14} /></button>
                                                                </RequirePermission>
                                                                <RequirePermission module="staff" action="delete">
                                                                    {staff.status === 'Active' ? (
                                                                        <button type="button" className={styles.iconBtnDanger} title="Deactivate" aria-label={`Deactivate ${staff.name}`} onClick={(e) => changeStatus(staff, 'deactivate', e)}><UserX size={14} /></button>
                                                                    ) : (
                                                                        <button type="button" className={styles.iconBtn} title="Make active again" aria-label={`Reactivate ${staff.name}`} onClick={(e) => changeStatus(staff, 'reactivate', e)}><RotateCcw size={14} /></button>
                                                                    )}
                                                                </RequirePermission>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            }) : (
                                                <tr>
                                                    <td colSpan="7" className={styles.emptyState}>
                                                        <User size={24} className={styles.mutedIcon} style={{ marginBottom: 8 }} />
                                                        <h3>No staff found</h3>
                                                        <p>{statusView === 'Active' ? 'Add a staff member or change the search.' : 'No inactive staff.'}</p>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div className={styles.rightPanel}>
                            {activeStaff ? (
                                <div className={styles.profileSidebar}>
                                    <div className={styles.profileHeader}>
                                        <div className={styles.profileAvatarLarge}>{initialsOf(activeStaff)}</div>
                                        <h2 className={styles.profileName}>{activeStaff.name}</h2>
                                        <span className={styles.profileRoleLabel}>{activeStaff.designation || 'Staff'}</span>
                                    </div>

                                    <div className={styles.profileContentScroll}>
                                        <div className={styles.sectionTitle}>Contact</div>
                                        <div className={styles.detailCard}>
                                            <div className={styles.detailRow}><Phone size={14} className={styles.mutedIcon} /> <span className={styles.detailText}>{activeStaff.phone || 'No phone'}</span></div>
                                            <div className={styles.detailRow}><Mail size={14} className={styles.mutedIcon} /> <span className={styles.detailText}>{activeStaff.email || 'No email'}</span></div>
                                        </div>

                                        <div className={styles.sectionTitle}>Work</div>
                                        <div className={styles.detailCard}>
                                            <div className={styles.detailRow}><Clock size={14} className={styles.mutedIcon} /> <span className={styles.detailText}>{to12(activeStaff.shift?.start)} to {to12(activeStaff.shift?.end)}</span></div>
                                            <div className={styles.detailRow}><CalendarDays size={14} className={styles.mutedIcon} /> <span className={styles.detailText}>
                                                Week off: {activeStaff.shift?.weeklyOffType === 'FIXED_DAYS' ? (activeStaff.shift.weeklyOffDays || []).map((d) => DAYS[d]).join(', ') || 'None' : activeStaff.shift?.weeklyOffType === 'ROTATIONAL' ? 'Any day' : 'None'}
                                            </span></div>
                                            {activeStaff.joiningDate && <div className={styles.detailRow}><UserCheck size={14} className={styles.mutedIcon} /> <span className={styles.detailText}>Joined {new Date(activeStaff.joiningDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span></div>}
                                        </div>

                                        <div className={styles.sectionTitle}>Account</div>
                                        <div className={styles.detailCard}>
                                            <div className={styles.statusRow}>
                                                {activeStaff.status === 'Active'
                                                    ? <><UserCheck size={16} color="var(--h-color-success-600)" /> <span className={styles.statusTextActive}>Active{activeStaff.hasAccess ? `, can log in${activeStaff.username ? ` as ${activeStaff.username}` : ''}` : ', no login'}</span></>
                                                    : <><UserMinus size={16} color="var(--text-secondary)" /> <span className={styles.statusTextInactive}>Inactive</span></>}
                                            </div>
                                        </div>
                                    </div>

                                    <div className={styles.sidebarFooter} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        <button type="button" className={styles.primaryBtnFull} onClick={() => navigate(`/staff/${activeStaff.id}`)}><Edit2 size={14} /> Full profile</button>
                                        {activeStaff.status === 'Active' && (
                                            <button type="button" className={styles.primaryBtnFull} onClick={() => navigate(`/attendance?staff=${activeStaff.id}`)}><CalendarDays size={14} /> Attendance</button>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className={styles.emptySidebarState}>
                                    <div className={styles.emptyPulseCircle}><User size={24} color="var(--h-color-neutral-400)" /></div>
                                    <h4>Select staff</h4>
                                    <p>Click a staff member for a quick view. Double-click to open the full profile.</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ManageStaff;